import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useMedications } from '../../hooks/useMedications'
import { useAuth } from '../../hooks/useAuth'
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore'
import { db } from '../../config/firebase'

interface LogEntry {
  timestamp: number
  level: 'info' | 'error' | 'success' | 'warning'
  message: string
}

const LiveLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [isVisible, setIsVisible] = useState(false)
  const [isDebugMode, setIsDebugMode] = useState(false)
  const pendingLogsRef = useRef<LogEntry[]>([])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { user } = useAuth()
  const { getMonthlyHistory } = useMedications()

  const batchUpdateLogs = useCallback(() => {
    if (pendingLogsRef.current.length > 0) {
      setLogs(prev => [...prev.slice(-20), ...pendingLogsRef.current].slice(-20)) // Keep last 20 logs
      pendingLogsRef.current = []
    }
  }, [])

  const addLog = useCallback((level: LogEntry['level'], message: string) => {
    const newLog: LogEntry = {
      timestamp: Date.now(),
      level,
      message
    }
    
    // Add to pending logs instead of immediately updating state
    pendingLogsRef.current.push(newLog)
    
    // Debounce the update to prevent setState during render
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    
    timeoutRef.current = setTimeout(() => {
      batchUpdateLogs()
    }, 100) // 100ms debounce
  }, [batchUpdateLogs])

  // FIXED: Added debug function to test historical data loading
  const debugHistoricalData = useCallback(async () => {
    if (!user?.uid) {
      addLog('error', '❌ No user authenticated - cannot debug historical data')
      return
    }

    addLog('info', '🔍 Starting historical data debug...')
    
    try {
      // Test direct database query
      const userHistoryCollection = collection(db, `users/${user.uid}/history`)
      const today = new Date()
      const currentMonth = today.getMonth()
      const currentYear = today.getFullYear()
      
      const startOfMonth = new Date(currentYear, currentMonth, 1)
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59)
      
      addLog('info', `📅 Querying range: ${startOfMonth.toISOString()} to ${endOfMonth.toISOString()}`)
      
      const historyQuery = query(
        userHistoryCollection,
        where('timestamp', '>=', startOfMonth),
        where('timestamp', '<=', endOfMonth),
        orderBy('timestamp', 'desc')
      )
      
      const snapshot = await getDocs(historyQuery)
      addLog('success', `✅ Direct query found ${snapshot.docs.length} documents`)
      
      if (snapshot.docs.length > 0) {
        const sampleDoc = snapshot.docs[0].data()
        addLog('info', `📄 Sample document structure: ${JSON.stringify({
          hasTimestamp: !!sampleDoc.timestamp,
          hasType: !!sampleDoc.type,
          hasStatus: !!sampleDoc.status,
          hasActualTime: !!sampleDoc.actualTime,
          type: sampleDoc.type,
          status: sampleDoc.status,
          medicationName: sampleDoc.medicationName
        })}`)
      }
      
      // Test the getMonthlyHistory function
      const activities = await getMonthlyHistory(currentYear, currentMonth)
      addLog('success', `✅ getMonthlyHistory returned ${activities.length} activities`)
      
      if (activities.length > 0) {
        const sampleActivity = activities[0]
        addLog('info', `📊 Sample processed activity: ${JSON.stringify({
          id: sampleActivity.id,
          medicationName: sampleActivity.medicationName,
          status: sampleActivity.status,
          hasActualTime: !!sampleActivity.actualTime,
          actualTime: sampleActivity.actualTime?.toISOString()
        })}`)
      }
      
    } catch (error) {
      addLog('error', `❌ Error debugging historical data: ${error}`)
    }
  }, [user?.uid, getMonthlyHistory, addLog])

  // Override console methods to capture logs
  useEffect(() => {
    const originalConsoleLog = console.log
    const originalConsoleError = console.error

    console.log = (...args: any[]) => {
      originalConsoleLog(...args)
      const message = args.join(' ')
      if (message.includes('🔍') || message.includes('🔄') || message.includes('✅') || message.includes('❌') || message.includes('🎉')) {
        addLog('info', message)
      }
    }

    console.error = (...args: any[]) => {
      originalConsoleError(...args)
      const message = args.join(' ')
      if (message.includes('❌')) {
        addLog('error', message)
      }
    }

    return () => {
      console.log = originalConsoleLog
      console.error = originalConsoleError
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [addLog])

  const getLogColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'info': return 'text-blue-300'
      case 'error': return 'text-red-300'
      case 'success': return 'text-green-300'
      case 'warning': return 'text-yellow-300'
      default: return 'text-gray-300'
    }
  }

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString()
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm font-medium mb-2 transition-colors"
      >
        {isVisible ? 'Hide' : 'Show'} Live Logs ({logs.length})
      </button>
      
      {isVisible && (
        <div className="bg-black bg-opacity-90 text-white p-4 rounded-lg text-xs max-w-md max-h-96 overflow-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-purple-400">🔴 Live Logs</h3>
            <button
              onClick={() => setIsDebugMode(!isDebugMode)}
              className="text-xs bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded"
            >
              {isDebugMode ? 'Hide' : 'Show'} Debug
            </button>
          </div>
          
          {/* FIXED: Added debug controls */}
          {isDebugMode && (
            <div className="mb-3 p-2 bg-gray-800 rounded">
              <div className="text-yellow-300 text-xs mb-2">🔧 Debug Controls:</div>
              <button
                onClick={debugHistoricalData}
                className="text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded mr-2"
              >
                Test Historical Data
              </button>
              <button
                onClick={() => setLogs([])}
                className="text-xs bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
              >
                Clear Logs
              </button>
            </div>
          )}
          
          <div className="space-y-1">
            {logs.map((log, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="text-gray-500 text-[10px] min-w-[60px]">
                  {formatTime(log.timestamp)}
                </span>
                <span className={`${getLogColor(log.level)} flex-1 break-words`}>
                  {log.message}
                </span>
              </div>
            ))}
            {logs.length === 0 && (
              <div className="text-gray-500 italic">No logs yet...</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default LiveLogs 